import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Heart, Play } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { photoSrc } from "@/components/Stars";
import PhotographerBadge from "@/components/PhotographerBadge";
import { likeHeaders } from "@/lib/useLikes";
import ShareMedia from "@/components/ShareMedia";
import Postcard from "@/components/Postcard";

const fetchTopMedia = async () => {
  const r = await fetch(`${API}/reviews/top-media?limit=5`, { credentials: "include", headers: likeHeaders() });
  if (!r.ok) throw new Error("top media failed");
  return r.json();
};

function MediaCard({ item, country, rank, lang, onSelect }) {
  const thumb = item.photo_url || item.video_poster_url;
  const name = country ? country.short_names[lang] : item.country_code;
  return (
    <div className="relative shrink-0 w-40 h-40 sm:w-44 sm:h-44">
    <button onClick={() => onSelect(item.country_code)} data-testid={`top-media-${item.id}`}
      className="relative w-full h-full rounded-2xl overflow-hidden border border-stoneLine bg-ink text-start group">
      {thumb ? (
        <img src={photoSrc(thumb)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : (
        <video src={photoSrc(item.video_url)} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <span className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent" />
      <span className={`absolute top-2 start-2 w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${rank === 0 ? "bg-ochre text-white" : "bg-sand/90 text-ink"}`}>{rank + 1}</span>
      <span className="absolute top-2 end-2 inline-flex items-center gap-1 rounded-full bg-rose-500 text-white text-[11px] font-semibold px-2 py-0.5" data-testid={`top-media-likes-${item.id}`}>
        <Heart className="w-3 h-3" fill="currentColor" /> {item.likes}
      </span>
      {item.video_url && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-9 h-9 rounded-full bg-sand/90 text-ink flex items-center justify-center shadow"><Play className="w-4 h-4 ms-0.5" fill="currentColor" /></span>
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 p-2.5 text-sand">
        <span className="flex items-center gap-1.5">
          {country && <img src={country.flag_url} alt="" className="w-5 h-3.5 object-cover rounded-sm border border-sand/40" />}
          <span className="text-xs font-semibold truncate">{name}</span>
        </span>
        <span className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span className="text-[11px] text-sand/80 truncate min-w-0">{item.author.name}</span>
          <PhotographerBadge show={item.is_photographer} />
        </span>
      </span>
    </button>
    <ShareMedia item={item} countryName={name} className="absolute top-10 end-2" testid={`top-media-share-${item.id}`} />
    <Postcard item={item} countryName={name} className="absolute top-[4.5rem] end-2" testid={`top-media-postcard-${item.id}`} />
    </div>
  );
}

/** Home strip: the 5 most-liked visitor photos/videos across the atlas. Click → open the country. */
export default function TopMedia({ countries, onSelect }) {
  const { lang, t } = useLang();
  const { data } = useQuery({ queryKey: ["top-media"], queryFn: fetchTopMedia, staleTime: 60_000 });
  const items = (data?.items || []).filter((i) => i.likes > 0);
  if (items.length === 0) return null;

  return (
    <div className="mt-6" data-testid="top-media">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Heart className="w-3 h-3 text-rose-500" fill="currentColor" /> {t("top_media")}
        {data?.photographer && (
          <span className="ms-auto normal-case tracking-normal flex items-center gap-1.5 text-ink" data-testid="photographer-of-month">
            <PhotographerBadge show /> <span className="font-semibold">{data.photographer.name}</span>
          </span>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto thin-scroll pb-2">
        {items.map((item, i) => (
          <MediaCard key={item.id} item={item} rank={i} lang={lang} onSelect={onSelect} country={countries.find((c) => c.code === item.country_code)} />
        ))}
      </div>
    </div>
  );
}
